import {
  Button,
  type ButtonProps,
  useDisclosure,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiXCircle } from "react-icons/fi";
import {
  BidV2Status,
  type BuyerBidV2DetailDto,
  useGetBidV2RejectionDocumentsQuery,
  useGetSourcingEventQuery,
  useRejectBidV2Mutation,
} from "../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useRunsPublicProcurements } from "../../../../common/organization/useRunsPublicProcurements";
import {
  type RejectionSubmission,
  RejectSupplierModal,
} from "../../../../common/rejection/RejectSupplierModal";
import { SectionCard } from "../../../sourcing-events/portal/view-single/SectionCard";
import { RejectedSummary } from "./BidRejectionSummary";

interface Props {
  detail: BuyerBidV2DetailDto;
  onViewRejectionDetails?: () => void;
}

export const BidRejectionActionCard = ({
  detail,
  onViewRejectionDetails,
}: Props) => {
  if (detail.bid.status === BidV2Status.Rejected) {
    return (
      <RejectedSummary detail={detail} onViewDetails={onViewRejectionDetails} />
    );
  }
  if (detail.bid.status !== BidV2Status.Submitted) {
    return null;
  }
  return <RejectAction detail={detail} />;
};

const RejectAction = ({ detail }: Props) => {
  const { t } = useTranslation();

  return (
    <SectionCard icon={FiXCircle} title={t("Actions")}>
      <RejectBidButton detail={detail} />
    </SectionCard>
  );
};

export const RejectBidButton = ({
  detail,
  size = "md",
  width = "full",
  label,
  onRejected,
}: {
  detail: BuyerBidV2DetailDto;
  size?: ButtonProps["size"];
  width?: ButtonProps["width"];
  label?: string;
  onRejected?: () => void;
}) => {
  const { t } = useTranslation();
  const disclosure = useDisclosure();

  return (
    <>
      <Button
        size={size}
        colorScheme="red"
        variant="outline"
        width={width}
        onClick={disclosure.onOpen}
      >
        {label ?? t("Avvis leverandør")}
      </Button>
      {disclosure.isOpen ? (
        <RejectBidModal
          detail={detail}
          isOpen={disclosure.isOpen}
          onClose={disclosure.onClose}
          onRejected={onRejected}
        />
      ) : null}
    </>
  );
};

const personName = (person: { firstName?: string | null; lastName?: string | null } | null | undefined) =>
  [person?.firstName, person?.lastName].filter(Boolean).join(" ") || null;

const RejectBidModal = ({
  detail,
  isOpen,
  onClose,
  onRejected,
}: {
  detail: BuyerBidV2DetailDto;
  isOpen: boolean;
  onClose: () => void;
  onRejected?: () => void;
}) => {
  const { triggerSoft } = useApiError();
  const [rejectBidV2, rejectState] = useRejectBidV2Mutation();
  const eventId = detail.bid.forEvent;
  const bidId = detail.bid.id;
  const { data, refetch } = useGetBidV2RejectionDocumentsQuery({
    eventId,
    bidId,
  });
  const runsPublicProcurements = useRunsPublicProcurements();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { data: event } = useGetSourcingEventQuery({ eventId }, { skip: runsPublicProcurements });
  const privateContext = runsPublicProcurements
    ? undefined
    : {
        eventTitle: detail.eventTitle ?? "",
        supplierContactName: detail.ownerName,
        responsibleBuyerName: personName(event?.responsiblePerson),
        buyerOrganizationName: selectedOrg.name,
      };

  const submit = async (submission: RejectionSubmission) => {
    try {
      await rejectBidV2({
        eventId,
        bidId,
        rejectBidV2RequestDto: submission,
      }).unwrap();
      onClose();
      onRejected?.();
    } catch (e) {
      triggerSoft(e);
    }
  };

  return (
    <RejectSupplierModal
      isOpen={isOpen}
      onClose={onClose}
      supplierName={detail.ownerName}
      organizationNumber={detail.ownerOrganizationNumber}
      foaPart={detail.foaPart}
      includeTenderGrounds
      privateContext={privateContext}
      attachments={{
        entityType: "BidV2Rejection",
        entityId: bidId,
        eventId,
        documents: data?.documents ?? [],
        refetch,
      }}
      onSubmit={submit}
      isSubmitting={rejectState.isLoading}
    />
  );
};
